import { CalendarOutlined, CloseOutlined, EnvironmentOutlined, FilePdfOutlined, MailOutlined, PhoneOutlined, UserOutlined, WhatsAppOutlined } from '@ant-design/icons';
import { Button, Checkbox, Col, ConfigProvider, DatePicker, Form, Modal, Row, Skeleton, Tabs, Tag, Typography, Spin } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import CustomerBalanceWidget from '../../components/CustomerBalanceWidget';
import { getCustomerDeliveryDays, getCustomerDetail, getCustomerStatementLink } from '../../redux/actions/customers';
import { formatAddress, formatCustomerDays, getAddressType } from '../../utils';
import KioskOrderHistory from './KioskOrderHistory';

const { Text } = Typography;

const KioskCustomerProfileModal = ({ isOpen, onCancel, customer }) => {
  const [balance, setBalance] = useState(null);
  const [fullCustomer, setFullCustomer] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [deliveryDays, setDeliveryDays] = useState('N/A');
  const [loading, setLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [statementLink, setStatementLink] = useState(null);
  const [isIframeLoading, setIsIframeLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [form] = Form.useForm();

  const customerId = customer?.customer_netsuite_id || customer?.netsuite_id;

  useEffect(() => {
    const fetchDetails = async () => {
      if (isOpen && customerId) {
        setLoading(true);
        form.resetFields();
        setStatementLink(null);
        setActiveTab('overview');
        try {
          const [res, daysRes] = await Promise.all([
            getCustomerDetail(customerId),
            getCustomerDeliveryDays(customerId)
          ]);

          if (res?.ack) {
            setBalance(res.customer_balance);
            setFullCustomer(res.customer_details?.[0] || customer);

            // Extract addresses
            const parsedAddresses = res.customer_details?.map((item) => {
              const { fullAddress } = formatAddress(item);
              return { ...item, fullAddress };
            }) || [];
            setAddresses(parsedAddresses);
          }

          if (daysRes) {
            const parsedDays = formatCustomerDays(daysRes?.delivery_days?.at(0) || {});
            setDeliveryDays(parsedDays?.deliveryDays || 'N/A');
          }
        } finally {
          setLoading(false);
        }
      }
    };
    fetchDetails();
  }, [isOpen, customerId, form]);

  const handleGenerate = async (values) => {
    if (!customerId) return;
    setIsGenerating(true);
    try {
      const payload = {
        start_date: dayjs(values.start_date).format('YYYY-MM-DD'),
        statement_date: dayjs(values.statement_date).format('YYYY-MM-DD'),
        customer_netsuite_id: customerId,
        show_open_transactions: values.show_open_transactions ? 1 : 0,
      };
      const res = await getCustomerStatementLink(payload);
      if (res?.ack && res.link) {
        setIsIframeLoading(true);
        setStatementLink(res.link);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const overviewTab = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '32px', paddingTop: '16px' }}>
      {balance && (
        <div className="kiosk-balance-wrapper" style={{ marginBottom: '8px' }}>
          <CustomerBalanceWidget balance={balance} isFullWidth />
        </div>
      )}

      <div style={{
        background: '#ffffff', border: '1px solid #e5e7eb',
        borderRadius: '16px', padding: '24px'
      }}>
        <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#374151', margin: '0 0 20px 0' }}>
          Generate Statement
        </h3>
        <Form
          form={form}
          layout="vertical"
          size="large"
          requiredMark={false}
          initialValues={{
            start_date: dayjs().subtract(1, 'month'),
            statement_date: dayjs(),
            show_open_transactions: true,
          }}
          onFinish={handleGenerate}
        >
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label={<Text strong style={{ color: '#4b5563' }}>Start Date</Text>}
                name="start_date"
                rules={[{ required: true, message: 'Select start date' }]}
              >
                <DatePicker showToday={false} style={{ width: '100%', borderRadius: '12px' }} format="DD-MM-YYYY" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={<Text strong style={{ color: '#4b5563' }}>Statement Date</Text>}
                name="statement_date"
                rules={[{ required: true, message: 'Select statement date' }]}
              >
                <DatePicker showToday={false} style={{ width: '100%', borderRadius: '12px' }} format="DD-MM-YYYY" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="show_open_transactions" valuePropName="checked" style={{ marginBottom: '24px' }}>
            <Checkbox style={{ fontSize: '16px', color: '#4b5563' }}>
              Only open transactions
            </Checkbox>
          </Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            loading={isGenerating}
            icon={<FilePdfOutlined />}
            style={{
              width: '100%', height: '56px', fontSize: '18px', fontWeight: 600,
              borderRadius: '28px', backgroundColor: '#F58220', borderColor: '#F58220'
            }}
          >
            Generate Statement
          </Button>
        </Form>
      </div>
    </div>
  );

  const detailsTab = (
    <div style={{ paddingTop: '16px', display: 'flex', flexDirection: 'column', gap: '32px' }}>

      {/* Contact & Delivery Card */}
      <div>
        <Text type="secondary" style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: '12px', display: 'block', paddingLeft: '4px' }}>Contact & Settings</Text>
        <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>

          {/* Email */}
          <div style={{ display: 'flex', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #f3f4f6' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '8px', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '16px', flexShrink: 0 }}>
              <MailOutlined style={{ fontSize: '18px', color: '#4b5563' }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <Text type="secondary" style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase' }}>Email</Text>
              <Text style={{ fontSize: '15px', fontWeight: 500, color: '#111827', marginTop: '2px' }}>{fullCustomer?.invoice_email || fullCustomer?.email || 'N/A'}</Text>
            </div>
          </div>

          {/* Phone */}
          <div style={{ display: 'flex', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #f3f4f6' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '8px', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '16px', flexShrink: 0 }}>
              <PhoneOutlined style={{ fontSize: '18px', color: '#4b5563' }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <Text type="secondary" style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase' }}>Phone</Text>
              <Text style={{ fontSize: '15px', fontWeight: 500, color: '#111827', marginTop: '2px' }}>{fullCustomer?.phone || 'N/A'}</Text>
            </div>
          </div>

          {/* WhatsApp */}
          <div style={{ display: 'flex', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #f3f4f6' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '8px', background: '#e8fbf0', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '16px', flexShrink: 0 }}>
              <WhatsAppOutlined style={{ fontSize: '18px', color: '#16a34a' }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <Text type="secondary" style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase' }}>WhatsApp</Text>
              <Text style={{ fontSize: '15px', fontWeight: 500, color: '#111827', marginTop: '2px' }}>{fullCustomer?.invoice_whatsapp_number || 'N/A'}</Text>
            </div>
          </div>

          {/* Delivery Days */}
          <div style={{ display: 'flex', alignItems: 'flex-start', padding: '16px 20px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '8px', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '16px', flexShrink: 0 }}>
              <CalendarOutlined style={{ fontSize: '18px', color: '#4b5563' }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', paddingTop: '2px' }}>
              <Text type="secondary" style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase' }}>Delivery Days</Text>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
                {Array.isArray(deliveryDays) && deliveryDays.length > 0
                  ? deliveryDays.map((d, i) => (
                    <Tag
                      key={i}
                      style={{ margin: 0, background: '#f3f4f6', border: 'none', color: '#4b5563', fontSize: '12px', padding: '4px 8px', borderRadius: '6px', fontWeight: 500 }}
                    >
                      {d.label}
                    </Tag>
                  ))
                  : <Text style={{ fontSize: '15px', fontWeight: 500, color: '#111827' }}>N/A</Text>
                }
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Addresses */}
      <div>
        <Text type="secondary" style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: '12px', display: 'block', paddingLeft: '4px' }}>Addresses</Text>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {addresses.length > 0 ? (
            addresses.map((addr, idx) => (
              <div key={addr.id || idx} style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '16px 20px', display: 'flex', alignItems: 'flex-start', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '8px', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '16px', flexShrink: 0 }}>
                  <EnvironmentOutlined style={{ fontSize: '18px', color: '#4b5563' }} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', paddingTop: '2px' }}>
                  <Text type="secondary" style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase' }}>{getAddressType(addr)}</Text>
                  <Text style={{ fontSize: '15px', fontWeight: 500, color: '#111827', marginTop: '4px', lineHeight: 1.5 }}>{addr.fullAddress}</Text>
                </div>
              </div>
            ))
          ) : (
            <div style={{ background: '#ffffff', border: '1px dashed #e5e7eb', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
              <Text type="secondary" style={{ fontSize: '14px' }}>No addresses found</Text>
            </div>
          )}
        </div>
      </div>

    </div>
  );

  const orderHistoryTab = (
    <div style={{ paddingTop: '16px' }}>
      <KioskOrderHistory customerId={customerId} onCloneComplete={onCancel} />
    </div>
  );

  return (
    <ConfigProvider theme={{ token: { colorPrimary: '#f58220' } }}>
      <Modal
        open={isOpen}
        onCancel={onCancel}
        footer={null}
        closable={true}
        closeIcon={<CloseOutlined style={{ fontSize: '24px', color: '#6b7280' }} />}
        maskClosable={true}
        centered
        width={activeTab === 'history' ? 1100 : 800}
        zIndex={2000}
        styles={{
          body: { padding: '32px 40px', minHeight: activeTab === 'history' ? '80vh' : undefined, display: 'flex', flexDirection: 'column' },
          content: { borderRadius: '24px', overflow: 'hidden', padding: 0 }
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', flex: 1 }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <div style={{
              width: '64px', height: '64px', borderRadius: '50%', background: '#fff7ed',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '32px', color: '#f58220', flexShrink: 0
            }}>
              <UserOutlined />
            </div>
            <div>
              <h2 style={{ fontSize: '28px', fontWeight: 700, color: '#111827', margin: 0, lineHeight: 1.2 }}>
                {fullCustomer?.title || fullCustomer?.company_name || customer?.title || customer?.company_name || 'Account Profile'}
              </h2>
              <p style={{ fontSize: '16px', color: '#6b7280', margin: '4px 0 0 0' }}>
                {fullCustomer?.invoice_email || fullCustomer?.email || fullCustomer?.phone || customer?.invoice_email || customer?.phone || 'Customer Details'}
              </p>
            </div>
          </div>

          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '28px', paddingTop: '16px', minHeight: '500px' }}>
              {/* Fake Tabs */}
              <div style={{ display: 'flex', gap: '32px', borderBottom: '1px solid #f3f4f6', paddingBottom: '12px' }}>
                <Skeleton.Button active size="small" style={{ width: '80px', height: '24px', borderRadius: '4px' }} />
                <Skeleton.Button active size="small" style={{ width: '80px', height: '24px', borderRadius: '4px' }} />
                <Skeleton.Button active size="small" style={{ width: '100px', height: '24px', borderRadius: '4px' }} />
              </div>

              {/* Fake Balance Widget */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                <Skeleton.Button active style={{ width: '100%', height: '120px', borderRadius: '16px' }} />
                <Skeleton.Button active style={{ width: '100%', height: '120px', borderRadius: '16px' }} />
                <Skeleton.Button active style={{ width: '100%', height: '120px', borderRadius: '16px' }} />
              </div>

              {/* Fake Form Area */}
              <div style={{ background: '#ffffff', border: '1px solid #f3f4f6', borderRadius: '16px', padding: '24px' }}>
                <Skeleton active title={{ width: '30%' }} paragraph={{ rows: 4, width: ['100%', '100%', '50%', '100%'] }} />
              </div>
            </div>
          ) : (
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              items={[
                { key: 'overview', label: 'Overview', children: overviewTab },
                { key: 'details', label: 'Details', children: detailsTab },
                { key: 'history', label: 'Order History', children: orderHistoryTab },
              ]}
              tabBarStyle={{ marginBottom: 0, fontWeight: 500, fontSize: '16px' }}
            />
          )}
        </div>
      </Modal>

      {/* PDF Statement Viewer Modal */}
      <Modal
        open={!!statementLink}
        onCancel={() => setStatementLink(null)}
        footer={null}
        closable={false}
        width={800}
        centered
        destroyOnHidden
        zIndex={2100}
        styles={{
          body: { height: '85vh', padding: 0, display: 'flex', flexDirection: 'column' },
          content: { padding: 0, overflow: 'hidden', borderRadius: '16px', background: '#ffffff' }
        }}
      >
        {/* Clean Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', background: '#ffffff', borderBottom: '1px solid #e5e7eb' }}>
          <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: '#111827' }}>Customer Statement</h3>
          <Button
            type="text"
            icon={<CloseOutlined style={{ fontSize: '20px' }} />}
            onClick={() => setStatementLink(null)}
            style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          />
        </div>

        {/* Iframe Container */}
        <div style={{ flex: 1, position: 'relative', overflow: 'hidden', background: '#ffffff' }}>
          {isIframeLoading && (
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}>
              <Spin size="large" />
            </div>
          )}
          {statementLink && (
            <iframe
              src={`${statementLink}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
              width="100%"
              height="100%"
              style={{ border: 'none', display: 'block', backgroundColor: '#ffffff' }}
              title="Customer Statement"
              onLoad={() => setIsIframeLoading(false)}
            />
          )}
        </div>
      </Modal>
    </ConfigProvider>
  );
};

export default KioskCustomerProfileModal;
